import type { FilterModel } from 'admin/src/types/Filter';
import type { ColumnState, GridApi, GridState } from 'ag-grid-enterprise';
import { convertColumnState } from 'ag-grid-enterprise';
import dayjs from 'dayjs';

export type DataGridColumnGridState = Pick<GridState, 'columnOrder' | 'columnVisibility' | 'columnPinning' | 'columnSizing'>;

export function getCellDataType(cellDataType?: string | boolean) {
  return (cellDataType === true ? 'boolean' : cellDataType) || 'text';
}

export function resolveColumnFilterModel(api: GridApi, columnId: string): FilterModel | null {
  if (api.isDestroyed()) return null;

  const fromColumn = api.getColumnFilterModel(columnId) as FilterModel | null;
  if (fromColumn) return fromColumn;

  const fromGrid = api.getFilterModel()?.[columnId] as FilterModel | undefined;
  return fromGrid ?? null;
}

export function getGridColumnStateStorageKey(gridId: string) {
  return `grid:${gridId}`;
}

function isLegacyColumnState(value: unknown): value is ColumnState[] {
  return Array.isArray(value);
}

function isColumnGridState(value: unknown): value is DataGridColumnGridState {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

export function readColumnGridStateFromLocalStorage(gridId: string): DataGridColumnGridState | undefined {
  const raw = window.localStorage.getItem(getGridColumnStateStorageKey(gridId));
  if (!raw) return undefined;

  try {
    const parsed: unknown = JSON.parse(raw);

    if (isLegacyColumnState(parsed)) {
      const converted = convertColumnState(parsed);
      const columnState: DataGridColumnGridState = {
        columnOrder: converted.columnOrder,
        columnVisibility: converted.columnVisibility,
        columnPinning: converted.columnPinning,
        columnSizing: converted.columnSizing,
      };

      window.localStorage.setItem(getGridColumnStateStorageKey(gridId), JSON.stringify(columnState));
      return columnState;
    }

    if (isColumnGridState(parsed)) {
      return {
        columnOrder: parsed.columnOrder,
        columnVisibility: parsed.columnVisibility,
        columnPinning: parsed.columnPinning,
        columnSizing: parsed.columnSizing,
      };
    }
  } catch {
    return undefined;
  }

  return undefined;
}

export function saveColumnGridStateToLocalStorage(gridId: string, state: GridState) {
  const columnState: DataGridColumnGridState = {
    columnOrder: state.columnOrder,
    columnVisibility: state.columnVisibility,
    columnPinning: state.columnPinning,
    columnSizing: state.columnSizing,
  };

  window.localStorage.setItem(getGridColumnStateStorageKey(gridId), JSON.stringify(columnState));
}

export function resetGridColumnState(api?: GridApi) {
  if (!api || api.isDestroyed()) return;

  window.localStorage.removeItem(getGridColumnStateStorageKey(api.getGridId()));
  api.resetColumnState();
}

export function standardDataGridDateComparator(filterValue: Date, cellValue: Date | string | null | undefined) {
  if (cellValue == null || cellValue === '') return -1;

  const cellDate = dayjs(cellValue).startOf('day');
  const filterDate = dayjs(filterValue).startOf('day');

  if (!cellDate.isValid() || !filterDate.isValid()) return -1;
  if (filterDate.isSame(cellDate, 'day')) return 0;

  return filterDate.isBefore(cellDate) ? 1 : -1;
}
